import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronLeft, MapPin, MoreHorizontal, Flag, Ban } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Avatar } from "@/components/Avatar";
import { ProfilePosts } from "@/components/profile/ProfilePosts";
import ReportButton from "@/components/ReportButton";
import { BlockConfirmDialog, UnblockButton, unblockUser } from "@/components/profile/BlockUserButton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function MemberMenu({ m, onBlocked, onUnblocked }) {
  const [report, setReport] = useState(false);
  const [block, setBlock] = useState(false);
  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label="Mais opções" data-testid="member-menu" className="h-10 w-10 inline-flex items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 active:bg-zinc-200 shrink-0">
            <MoreHorizontal size={20} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 rounded-xl" data-testid="member-menu-content">
          <DropdownMenuItem onSelect={() => setReport(true)} data-testid="member-menu-report" className="gap-2 py-2.5"><Flag size={15} /> Denunciar utilizador</DropdownMenuItem>
          {m.blocked_by_me
            ? <DropdownMenuItem onSelect={onUnblocked} data-testid="member-menu-unblock" className="gap-2 py-2.5"><Ban size={15} /> Desbloquear utilizador</DropdownMenuItem>
            : <DropdownMenuItem onSelect={() => setBlock(true)} data-testid="member-menu-block" className="gap-2 py-2.5 text-[#c8102e] focus:text-[#c8102e]"><Ban size={15} /> Bloquear utilizador</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      <ReportButton target_type="user" target_id={m.id} hideTrigger open={report} onOpenChange={setReport} />
      <BlockConfirmDialog open={block} onOpenChange={setBlock} userId={m.id} userName={m.name} onBlocked={onBlocked} />
    </>
  );
}

export default function MemberProfile() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const highlightId = params.get("post");
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => api.get(`/members/${id}`).then(({ data }) => setM(data)).catch((e) => setErr(e.response?.status || 500));
  useEffect(() => { setM(null); setErr(null); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) {
    return (
      <div className="px-4 pt-6 pb-6 max-w-2xl mx-auto" data-testid="member-not-found">
        <Link to="/comunidade" className="inline-flex items-center gap-1 text-sm text-zinc-600 mb-6"><ChevronLeft size={16} /> Voltar</Link>
        <div className="bg-white rounded-2xl border border-zinc-200 p-8 text-center text-sm text-zinc-600">Perfil não disponível.</div>
      </div>
    );
  }
  if (!m) return <div className="px-4 pt-6 text-sm text-zinc-500">A carregar…</div>;

  const unblock = async () => {
    try { await unblockUser(m.id); toast.success("Utilizador desbloqueado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erro"); }
  };

  return (
    <div className="px-4 pt-4 pb-6 max-w-2xl mx-auto" data-testid="member-profile">
      <button type="button" onClick={() => window.history.back()} className="inline-flex items-center gap-1 text-sm text-zinc-600 mb-4" data-testid="member-back"><ChevronLeft size={16} /> Voltar</button>
      <div className="bg-white rounded-2xl border border-zinc-200 p-4 mb-6 flex items-start gap-4">
        <Avatar src={m.photo_url} name={m.name} size={72} testId="member-avatar" />
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-xl font-extrabold leading-tight break-words" data-testid="member-name">{m.name}</h1>
          {m.city && <div className="text-xs text-zinc-500 inline-flex items-center gap-1 mt-0.5" data-testid="member-city"><MapPin size={12} /> {m.city}</div>}
          {m.bio && !m.blocked_by_me && <p className="text-sm text-zinc-700 mt-2 whitespace-pre-wrap break-words" data-testid="member-bio">{m.bio}</p>}
          {m.is_me && <Link to="/perfil" className="inline-block mt-2 text-xs font-semibold underline" data-testid="member-edit-link">Editar o meu perfil</Link>}
        </div>
        {!m.is_me && <MemberMenu m={m} onBlocked={load} onUnblocked={unblock} />}
      </div>
      {m.blocked_by_me ? (
        <div className="bg-zinc-50 rounded-2xl border border-zinc-200 p-5 text-center" data-testid="member-blocked-banner">
          <Ban size={22} className="mx-auto text-zinc-400 mb-2" />
          <p className="text-sm font-semibold">Bloqueaste este utilizador</p>
          <p className="text-xs text-zinc-500 mt-1 mb-3">As publicações e interações entre vocês estão ocultas em ambos os sentidos.</p>
          <UnblockButton userId={m.id} onDone={load} />
        </div>
      ) : (
        <ProfilePosts userId={m.id} isOwner={m.is_me} highlightId={highlightId} />
      )}
    </div>
  );
}
